<template>
  <div class="garden-view">
    <!-- Header -->
    <div class="garden-header">
      <div class="garden-header__inner">
        <!-- Title -->
        <div class="garden-header__title-area">
          <h1 class="garden-header__title">
            <span class="garden-header__emoji">&#127793;</span>
            {{ studentName }}'s Learning Garden
          </h1>
          <p class="garden-header__subtitle">Watch your knowledge bloom!</p>
        </div>

        <!-- Progress Ring -->
        <div class="garden-header__ring">
          <svg width="130" height="130" viewBox="0 0 130 130">
            <circle cx="65" cy="65" r="56" class="garden-header__ring-bg" stroke-width="12" fill="none" />
            <circle cx="65" cy="65" r="56" stroke="url(#gardenRingGrad)" stroke-width="12" fill="none"
                    :stroke-dasharray="circumference" :stroke-dashoffset="ringOffset" stroke-linecap="round"
                    class="garden-header__ring-fill" />
            <defs>
              <linearGradient id="gardenRingGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stop-color="#10b981" />
                <stop offset="100%" stop-color="#8b5cf6" />
              </linearGradient>
            </defs>
          </svg>
          <div class="garden-header__ring-label">
            <span class="garden-header__ring-pct">{{ securePlusPct }}%</span>
            <span class="garden-header__ring-sub">Secure+</span>
          </div>
        </div>

        <!-- Mini Stats -->
        <div class="garden-header__stats">
          <div class="garden-header__pill">
            <MTIcon name="sprout" :size="16" class="garden-header__pill-icon garden-header__pill-icon--green" />
            <span class="garden-header__pill-text garden-header__pill-text--green">{{ growingCount }} topics growing</span>
          </div>
          <div class="garden-header__pill">
            <MTIcon name="flower-2" :size="16" class="garden-header__pill-icon garden-header__pill-icon--purple" />
            <span class="garden-header__pill-text garden-header__pill-text--purple">{{ masteryCount }} in full bloom</span>
          </div>
          <div class="garden-header__pill">
            <span class="garden-header__pill-emoji">&#128293;</span>
            <span class="garden-header__pill-text garden-header__pill-text--orange">{{ streakEngine.streakLabel.value }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Main content: garden + sidebar -->
    <div class="garden-main">
      <!-- Garden Grid -->
      <div class="garden-grid-area">
        <h2 class="garden-grid-area__heading">
          <MTIcon name="layout-grid" :size="20" />
          My Garden
        </h2>
        <div class="garden-grid">
          <template v-for="plant in gardenPlants" :key="plant.lessonId">
            <!-- Accessible plant -->
            <RouterLink
              v-if="!plant.locked"
              :to="`/student/${childId}/learn/${plant.lessonId}`"
              class="garden-grid__cell"
            >
              <PlantPot :band="plant.band" :topic-name="plant.lessonTitle" />
            </RouterLink>

            <!-- Locked plant -->
            <div
              v-else
              class="garden-grid__cell garden-grid__cell--locked"
              :title="plant.lockMessage"
              @click="showUpgradeToast(plant.lockMessage)"
            >
              <PlantPot :band="plant.band" :topic-name="plant.lessonTitle" />
              <div class="garden-pot-lock-overlay">
                <div class="garden-pot-lock-icon">
                  <MTIcon name="lock" :size="20" />
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>

      <!-- Sidebar -->
      <div class="garden-sidebar-area">
        <GardenSidebar :child-id="childId" />
      </div>
    </div>

    <!-- Upgrade toast -->
    <Transition name="garden-toast">
      <div v-if="upgradeToast" class="garden-upgrade-toast">
        <MTIcon name="lock" :size="16" class="flex-shrink-0" />
        <span class="flex-1">{{ upgradeToast }}</span>
        <RouterLink to="/parent/subscription" class="garden-toast-btn">Upgrade</RouterLink>
      </div>
    </Transition>

    <!-- Bottom encouragement -->
    <div class="garden-cta">
      <div class="garden-cta__content">
        <span class="garden-cta__emoji">&#127775;</span>
        <div>
          <p class="garden-cta__title">Keep watering your garden!</p>
          <p class="garden-cta__desc">{{ ctaMessage }}</p>
        </div>
      </div>
      <RouterLink :to="waterLink" class="garden-cta__btn">
        Water a plant
        <MTIcon name="arrow-right" :size="16" />
      </RouterLink>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import MTIcon from '@/components/ui/MTIcon.vue';
import PlantPot from './PlantPot.vue';
import GardenSidebar from './GardenSidebar.vue';
import { useCurriculumStore } from '@/stores/curriculumStore';
import { useProgressStore } from '@/stores/progressStore';
import { useStudentStore } from '@/stores/studentStore';
import { useUserStore } from '@/stores/userStore';
import { useStreakEngine } from '@/composables/useStreakEngine';
import { isLessonLocked, requiredPlanLabel } from '@/composables/usePlanGating';

const route = useRoute();
const curriculumStore = useCurriculumStore();
const progressStore = useProgressStore();
const studentStore = useStudentStore();

const childId = computed(() => route.params.studentId || '');
const student = computed(() => studentStore.students.find(s => s.id === childId.value));
const userStore = useUserStore();

const upgradeToast = ref(null);
let upgradeToastTimer = null;
function showUpgradeToast(msg) {
  upgradeToast.value = msg;
  clearTimeout(upgradeToastTimer);
  upgradeToastTimer = setTimeout(() => { upgradeToast.value = null; }, 4000);
}

onMounted(async () => {
  if (!curriculumStore.modules.length) {
    await curriculumStore.loadTrack(student.value?.track || 'eplus_core');
  }
  progressStore.ensureChild(childId.value);
});
const studentName = computed(() => student.value?.name || 'Your');

const streakEngine = useStreakEngine(childId.value);

const circumference = 2 * Math.PI * 56; // ~351.86

// Band numeric values for averaging (same scale as LessonHubPage)
const BAND_VALUES = { beginner: 0, developing: 50, secure: 70, confident: 85, mastery: 100 };

function getLessonBand(lesson) {
  const subs = lesson.subsections || [];
  if (!subs.length) return 'beginner';
  const avg = subs.reduce((sum, sub) => {
    return sum + (BAND_VALUES[progressStore.getSectionGroupBand(childId.value, sub.content)] || 0);
  }, 0) / subs.length;
  if (avg >= 86) return 'mastery';
  if (avg >= 75) return 'confident';
  if (avg >= 65) return 'secure';
  if (avg >= 40) return 'developing';
  return 'beginner';
}

// One plant per lesson, band derived from average of its topics
const gardenPlants = computed(() => {
  return curriculumStore.allLessons.map(lesson => {
    const locked = isLessonLocked(lesson.excludedPlans, userStore.effectivePlan);
    const requiredPlan = locked ? requiredPlanLabel(lesson.excludedPlans) : null;
    return {
      lessonId: lesson.id,
      lessonTitle: lesson.title,
      band: getLessonBand(lesson),
      locked,
      lockMessage: requiredPlan ? `Upgrade to ${requiredPlan} to unlock this lesson` : 'Upgrade your plan to unlock this lesson'
    };
  });
});

// Stats
const securePlusPct = computed(() => {
  if (!gardenPlants.value.length) return 0;
  const secureUp = gardenPlants.value.filter(p => ['secure', 'confident', 'mastery'].includes(p.band));
  return Math.round((secureUp.length / gardenPlants.value.length) * 100);
});

const ringOffset = computed(() => {
  return circumference - (securePlusPct.value / 100) * circumference;
});

const growingCount = computed(() => {
  return gardenPlants.value.filter(p => ['developing', 'secure', 'confident'].includes(p.band)).length;
});

const masteryCount = computed(() => {
  return gardenPlants.value.filter(p => p.band === 'mastery').length;
});

const belowSecureCount = computed(() => {
  return gardenPlants.value.filter(p => ['beginner', 'developing'].includes(p.band)).length;
});

const ctaMessage = computed(() => {
  if (belowSecureCount.value > 0) {
    return `${belowSecureCount.value} more lesson${belowSecureCount.value === 1 ? '' : 's'} need${belowSecureCount.value === 1 ? 's' : ''} attention to reach Secure level`;
  }
  return 'All lessons are growing nicely!';
});

const waterLink = computed(() => `/student/${childId.value}/dashboard`);
</script>

<style scoped>
.garden-view {
  max-width: 80rem;
  margin: 0 auto;
  padding: 1rem;
}

/* Header */
.garden-header {
  border-radius: 1.5rem;
  padding: 1.5rem 2rem;
  margin-bottom: 2rem;
  box-shadow: 0 4px 12px rgba(0,0,0,0.08);
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 50%, #6ee7b7 100%);
}
.garden-header__inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
}
@media (min-width: 768px) {
  .garden-header__inner { flex-direction: row; }
  .garden-header__title-area { flex: 1; text-align: left; }
}
.garden-header__title-area { text-align: center; }
.garden-header__title {
  font-size: 1.75rem;
  font-weight: 800;
  color: #064e3b;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}
@media (min-width: 768px) {
  .garden-header__title { justify-content: flex-start; font-size: 2rem; }
}
.garden-header__emoji { font-size: 2rem; }
.garden-header__subtitle { color: #047857; margin-top: 0.5rem; font-size: 1.1rem; }

/* Ring */
.garden-header__ring { position: relative; flex-shrink: 0; }
.garden-header__ring-bg { stroke: #d1fae5; }
.garden-header__ring-fill {
  transition: stroke-dashoffset 0.5s ease;
  transform: rotate(-90deg);
  transform-origin: 50% 50%;
}
.garden-header__ring-label {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
.garden-header__ring-pct { font-size: 1.75rem; font-weight: 800; color: #065f46; }
.garden-header__ring-sub { font-size: 0.75rem; color: #059669; font-weight: 600; }

/* Stat pills */
.garden-header__stats { display: flex; flex-wrap: wrap; justify-content: center; gap: 0.75rem; }
.garden-header__pill {
  background: rgba(255,255,255,0.7);
  backdrop-filter: blur(4px);
  border-radius: 9999px;
  padding: 0.5rem 1rem;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.garden-header__pill-icon--green { color: #059669; }
.garden-header__pill-icon--purple { color: #7c3aed; }
.garden-header__pill-text { font-weight: 700; font-size: 0.875rem; }
.garden-header__pill-text--green { color: #065f46; }
.garden-header__pill-text--purple { color: #581c87; }
.garden-header__pill-text--orange { color: #9a3412; }
.garden-header__pill-emoji { font-size: 0.875rem; }

/* Main layout */
.garden-main {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}
@media (min-width: 1024px) {
  .garden-main { flex-direction: row; }
  .garden-grid-area { flex: 1; }
  .garden-sidebar-area { width: 20rem; flex-shrink: 0; }
}

.garden-grid-area__heading {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--mt-text-primary, #064e3b);
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

/* Grid: responsive columns */
.garden-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
}
@media (min-width: 640px) {
  .garden-grid { grid-template-columns: repeat(3, 1fr); }
}
@media (min-width: 1024px) {
  .garden-grid { grid-template-columns: repeat(4, 1fr); }
}

/* Grid cell wrapper */
.garden-grid__cell {
  display: block;
  position: relative;
  text-decoration: none;
  color: inherit;
}

/* Locked plant */
.garden-grid__cell--locked {
  opacity: 0.55;
  cursor: not-allowed;
  filter: grayscale(0.6);
}

/* Lock overlay */
.garden-pot-lock-overlay {
  position: absolute;
  inset: 0;
  border-radius: 1rem;
  background: rgba(0, 0, 0, 0.28);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
  pointer-events: none;
}
.garden-pot-lock-icon {
  background: rgba(220, 38, 38, 0.9);
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  box-shadow: 0 2px 8px rgba(0,0,0,0.3);
}

/* Upgrade toast */
.garden-upgrade-toast {
  position: fixed;
  bottom: 1.5rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: #1e293b;
  color: #f1f5f9;
  border: 1px solid #334155;
  border-radius: 0.875rem;
  padding: 0.75rem 1rem;
  box-shadow: 0 8px 30px rgba(0,0,0,0.35);
  z-index: 9999;
  min-width: 280px;
  max-width: 90vw;
  font-size: 0.875rem;
}
.garden-toast-btn {
  background: #f59e0b;
  color: #1c1917;
  font-weight: 700;
  font-size: 0.75rem;
  padding: 0.3rem 0.75rem;
  border-radius: 0.5rem;
  white-space: nowrap;
  text-decoration: none;
}
.garden-toast-btn:hover { background: #d97706; }

.garden-toast-enter-active, .garden-toast-leave-active { transition: all 0.25s ease; }
.garden-toast-enter-from, .garden-toast-leave-to { opacity: 0; transform: translateX(-50%) translateY(12px); }

/* CTA */
.garden-cta {
  margin-top: 2rem;
  border-radius: 1rem;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  background: linear-gradient(135deg, #fef9c3, #fde68a, #fbbf24);
}
@media (min-width: 640px) {
  .garden-cta { flex-direction: row; }
}
.garden-cta__content { display: flex; align-items: center; gap: 0.75rem; }
.garden-cta__emoji { font-size: 1.875rem; }
.garden-cta__title { font-weight: 800; color: #78350f; font-size: 1.1rem; }
.garden-cta__desc { color: #92400e; font-size: 0.875rem; }
.garden-cta__btn {
  background: #78350f;
  color: white;
  font-weight: 700;
  padding: 0.75rem 1.5rem;
  border-radius: 9999px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  white-space: nowrap;
  text-decoration: none;
  transition: background 0.2s;
}
.garden-cta__btn:hover { background: #92400e; }
</style>

<!-- Theme overrides (non-scoped) -->
<style>
/* Space theme */
.theme-space .garden-view { color: #e2e8f0; }
.theme-space .garden-header {
  background: linear-gradient(135deg, rgba(6,78,59,0.6), rgba(20,83,45,0.4));
  border: 1px solid rgba(52, 211, 153, 0.2);
}
.theme-space .garden-header__title { color: #6ee7b7; }
.theme-space .garden-header__subtitle { color: rgba(110, 231, 183, 0.7); }
.theme-space .garden-header__ring-bg { stroke: #1e3a2f; }
.theme-space .garden-header__ring-pct { color: #6ee7b7; }
.theme-space .garden-header__ring-sub { color: #34d399; }
.theme-space .garden-header__pill {
  background: rgba(6, 78, 59, 0.5);
  border: 1px solid rgba(52, 211, 153, 0.2);
}
.theme-space .garden-header__pill-text--green { color: #6ee7b7; }
.theme-space .garden-header__pill-text--purple { color: #c4b5fd; }
.theme-space .garden-header__pill-text--orange { color: #fdba74; }
.theme-space .garden-grid-area__heading { color: #6ee7b7; }
.theme-space .garden-cta {
  background: linear-gradient(135deg, rgba(120, 53, 15, 0.4), rgba(146, 64, 14, 0.3));
  border: 1px solid rgba(251, 191, 36, 0.2);
}
.theme-space .garden-cta__title { color: #fde68a; }
.theme-space .garden-cta__desc { color: #fcd34d; }
.theme-space .garden-cta__btn { background: #b45309; }
.theme-space .garden-cta__btn:hover { background: #d97706; }

/* Trail theme */
.theme-trail .garden-header {
  background: linear-gradient(135deg, #fef9c3 0%, #fde68a 50%, #d9f99d 100%);
}
.theme-trail .garden-header__title { color: #3f6212; }
.theme-trail .garden-header__subtitle { color: #4d7c0f; }
.theme-trail .garden-header__ring-pct { color: #3f6212; }
.theme-trail .garden-cta {
  background: linear-gradient(135deg, #ecfccb, #d9f99d, #a3e635);
}

/* Road theme */
.theme-road .garden-header {
  background: linear-gradient(135deg, #ccfbf1 0%, #99f6e4 50%, #5eead4 100%);
}
.theme-road .garden-header__title { color: #134e4a; }
.theme-road .garden-header__subtitle { color: #0f766e; }
.theme-road .garden-header__ring-pct { color: #134e4a; }
</style>
